/**
 * Inline registry credential status for the workload editor.
 *
 * Renders next to the workload's ``image`` input so operators can see
 * — at a glance — whether the configured image will pull on every
 * replica. Three states:
 *
 *   - no image yet                                 → render nothing
 *   - image's registry has a configured credential → ✓ green pill
 *   - image's registry has no credential           → ⚠ amber pill with
 *                                                    a one-click "add
 *                                                    credential" form
 *
 * The "add credential" path opens a small modal pre-filled with the
 * detected host. Saving POSTs to ``/api/v1/container-registries``
 * (global scope) which immediately re-projects to
 * ``~/.docker/config.json`` on the controller; the indicator flips to
 * green on the next query refresh.
 *
 * Operators who want full CRUD — rotating secrets, deleting rows,
 * per-service overrides — click the "manage all" link, which routes
 * to the Global Container Registries tab.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Container,
  ExternalLink,
  KeyRound,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";

import {
  REGISTRY_SUGGESTIONS,
  useContainerRegistries,
  useCreateContainerRegistry,
  useDeleteContainerRegistry,
  useUpdateContainerRegistry,
} from "@/api/local/container_registries";
import type {
  ContainerRegistryAuthOut,
  ContainerRegistryAuthUpsertBody,
} from "@/api/types";
import { useConfirm } from "@/components/ConfirmDialog";
import { Modal } from "@/components/Modal";
import { Pill } from "@/components/Pill";

/**
 * Docker Hub goes by several names across SDKs and exposition formats:
 *
 *   - ``docker.io``               — the operator-facing shorthand
 *                                   (what the WebUI suggestion list +
 *                                   the renderer's ``parse_image_host``
 *                                   normalise to).
 *   - ``registry-1.docker.io``    — the actual v2 manifest endpoint
 *                                   (what the controller's ``image_probe``
 *                                   resolves to).
 *   - ``index.docker.io``         — historical alias still seen in some
 *                                   tooling.
 *
 * Treat all three as the same registry for matching purposes so an
 * operator who registers a ``docker.io`` credential gets a green pill
 * regardless of which alias the parser produces.
 */
const DOCKER_HUB_ALIASES = new Set([
  "docker.io",
  "registry-1.docker.io",
  "index.docker.io",
]);

function isDockerHub(host: string): boolean {
  return DOCKER_HUB_ALIASES.has(host.toLowerCase());
}

function hostsMatch(a: string, b: string): boolean {
  const ax = a.toLowerCase();
  const bx = b.toLowerCase();
  if (ax === bx) return true;
  if (isDockerHub(ax) && isDockerHub(bx)) return true;
  return false;
}

/**
 * Mirror of ``controller.skypilot.registry_auth.parse_image_host`` so
 * the WebUI's host detection and the renderer's auth resolution agree
 * on a canonical name. The Docker reference grammar is small enough
 * that re-implementing it is cheaper than threading a server call.
 */
export function parseImageHost(image: string | null | undefined): string {
  const trimmed = (image ?? "").trim();
  if (!trimmed) return "";
  // Slice off any tag (`:tag`) or digest (`@sha256:…`) *after* peeking
  // at the first path segment — otherwise the colon inside a tag would
  // make a Docker Hub image look like a port-bearing registry host.
  const head = trimmed.split("/", 1)[0];
  if (head.includes(".") || head.includes(":") || head === "localhost") {
    return head;
  }
  return "docker.io";
}

interface Props {
  image: string | null | undefined;
  /** Layout hint — defaults to inline (sits flush with the image hint). */
  variant?: "inline" | "block";
}

type EditorMode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; row: ContainerRegistryAuthOut };

export function RegistryAuthIndicator({ image, variant = "inline" }: Props) {
  const host = useMemo(() => parseImageHost(image), [image]);
  const list = useContainerRegistries();
  const deleteRegistry = useDeleteContainerRegistry();
  const confirm = useConfirm();
  const [editor, setEditor] = useState<EditorMode>({ kind: "closed" });

  // No image typed yet -> nothing to show. Hiding (rather than
  // rendering a placeholder) keeps the field hints below the input
  // tight while the operator is still composing the image string.
  if (!host) return null;

  const matched = (list.data ?? []).find(
    (row) => row.scope === "global" && hostsMatch(row.registry, host),
  );
  const matchedEnabled = matched && matched.enabled;

  const onRemove = async (row: ContainerRegistryAuthOut) => {
    const ok = await confirm({
      title: "Delete registry credential?",
      tone: "danger",
      confirmLabel: "Delete",
      description: (
        <>
          <p style={{ marginTop: 0 }}>
            Delete registry credential <strong>{row.name}</strong>{" "}
            <span className="muted">
              (<code className="mono">{row.registry}</code>)
            </span>
            ?
          </p>
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            Any service or pool whose image pulls from{" "}
            <code className="mono">{row.registry}</code> will fall back to
            anonymous access on the next replica replace.
          </p>
        </>
      ),
    });
    if (!ok) return;
    void deleteRegistry(row.id);
  };

  const containerStyle: React.CSSProperties =
    variant === "inline"
      ? {
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 6,
          flexWrap: "wrap",
          fontSize: 12,
        }
      : {
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "8px 0",
          flexWrap: "wrap",
          fontSize: 12,
        };

  // Building the "open Global Container Registries tab" URL is just
  // ``?tab=container-registries`` because the workloads route reads
  // the scope from ``?focus=...`` (default = global). We keep the link
  // navigation native so middle-click "open in new tab" works.
  const manageHref = "/?tab=container-registries";

  return (
    <>
      <div style={containerStyle}>
        {matchedEnabled ? (
          <>
            <Pill tone="ok">
              <ShieldCheck
                style={{
                  width: 12,
                  height: 12,
                  marginRight: 4,
                  verticalAlign: "middle",
                }}
              />
              auth: {matched.name}
            </Pill>
            <span className="muted">
              <code>{host}</code> credential configured
              {matched.password_last4 && (
                <>
                  {" "}
                  (PAT ending in <code>…{matched.password_last4}</code>)
                </>
              )}
              .
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              title="Rotate password / rename / disable"
              onClick={() => setEditor({ kind: "edit", row: matched })}
            >
              <Pencil />
              rotate
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm btn--danger"
              title="Delete this credential"
              onClick={() => {
                void onRemove(matched);
              }}
            >
              <Trash2 />
              remove
            </button>
          </>
        ) : matched ? (
          <>
            <Pill tone="warn">credential disabled</Pill>
            <span className="muted">
              A credential for <code>{host}</code> exists but is{" "}
              <strong>disabled</strong> — re-enable it or replace it
              below.
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setEditor({ kind: "edit", row: matched })}
            >
              <Pencil />
              edit
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm btn--danger"
              onClick={() => {
                void onRemove(matched);
              }}
            >
              <Trash2 />
              remove
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setEditor({ kind: "create" })}
            >
              <Plus />
              add another
            </button>
          </>
        ) : (
          <>
            <Pill tone={isPublicLikely(host) ? "neutral" : "warn"}>
              no credential
            </Pill>
            <span className="muted">
              {isPublicLikely(host) ? (
                <>
                  Pulls from <code>{host}</code> use anonymous access.
                  That works for public images; add a credential to
                  pull private repositories.
                </>
              ) : (
                <>
                  No <code>docker login</code> for <code>{host}</code>.
                  Add one so SkyPilot can pull this image onto every
                  replica.
                </>
              )}
            </span>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setEditor({ kind: "create" })}
            >
              <KeyRound />
              add credential
            </button>
          </>
        )}
        <span style={{ flex: 1 }} />
        <a
          href={manageHref}
          className="muted"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
          }}
        >
          <Container style={{ width: 12, height: 12 }} />
          manage all
          <ExternalLink style={{ width: 11, height: 11 }} />
        </a>
      </div>
      <InlineRegistryEditor
        open={editor.kind !== "closed"}
        host={host}
        existing={editor.kind === "edit" ? editor.row : undefined}
        onClose={() => setEditor({ kind: "closed" })}
      />
    </>
  );
}

/**
 * Best-effort "is this a public registry?" heuristic. We can't tell
 * for certain without trying a pull, so we only use this to pick
 * between an amber "no credential, fix me" pill and a neutral
 * "anonymous access" pill — the green pill always requires a real
 * matching row.
 */
function isPublicLikely(host: string): boolean {
  const lower = host.toLowerCase();
  if (isDockerHub(lower)) return true;
  if (lower === "ghcr.io") return false; // GHCR defaults to private on push
  // Free, ephemeral registry used by `loadtest-image` workflow for
  // throw-away pushes — always anonymous.
  if (lower === "ttl.sh") return true;
  return false;
}

interface InlineEditorProps {
  open: boolean;
  host: string;
  /**
   * When provided, the form goes into edit mode: name/host/username are
   * pre-filled from the row, password is left blank ("leave empty to
   * keep the stored value"), and submit issues a PUT instead of a POST.
   */
  existing?: ContainerRegistryAuthOut;
  onClose: () => void;
}

/**
 * Inline create / edit form for a global-scope credential. Keeps the
 * surface narrow on purpose — name / username / password — so the
 * "I just need to pull this private image" path doesn't require a
 * round-trip through the global page. Reused for "rotate" by passing
 * an ``existing`` row.
 *
 * The host field is editable (defaults to the detected host) so the
 * operator can correct a typo without backing out; the suggestion
 * datalist is the same one the global page uses.
 */
function InlineRegistryEditor({
  open,
  host,
  existing,
  onClose,
}: InlineEditorProps) {
  const create = useCreateContainerRegistry();
  const update = useUpdateContainerRegistry();
  const editing = existing !== undefined;
  const [draft, setDraft] = useState<ContainerRegistryAuthUpsertBody>(() =>
    buildInitialDraft(host, existing),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset whenever the modal is reopened or the target row changes
  // (so popping the modal twice for two different images doesn't carry
  // over the previous attempt, and switching from "rotate" on row A
  // to "add" doesn't leave A's name in the field).
  //
  // We deliberately use ``useEffect`` rather than ``useMemo`` here:
  // useMemo runs during render, so calling setState inside it triggers
  // a follow-up render even when nothing observable changed and can
  // mask state updates from elsewhere. The reset is a side-effect, so
  // it belongs in an effect.
  useEffect(() => {
    if (!open) return;
    setDraft(buildInitialDraft(host, existing));
    setError(null);
  }, [open, host, existing]);

  const canSubmit =
    !!draft.name &&
    !!draft.registry &&
    !!draft.username &&
    // On edit, password is optional (blank = keep existing).
    (editing || !!draft.password);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      if (editing && existing) {
        // Strip the password when blank so the controller keeps the
        // currently stored ciphertext instead of trying to re-wrap
        // an empty string.
        const body: ContainerRegistryAuthUpsertBody = { ...draft };
        if (!body.password) delete body.password;
        await update(existing.id, body);
      } else {
        await create(draft);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "save failed");
    } finally {
      setBusy(false);
    }
  };

  const title = editing
    ? `Edit credential ${existing.name} (${existing.registry})`
    : `Add registry credential for ${host}`;
  const submitLabel = editing
    ? draft.password
      ? "rotate & save"
      : "save changes"
    : "create credential";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={
        <span className="muted" style={{ fontSize: 12 }}>
          Stored globally — applies to every workload image with a
          matching host. Password is encrypted at rest and never
          round-trips to the browser after save.
          {editing && (
            <>
              {" "}
              Leave the password field blank to keep the currently
              stored value.
            </>
          )}
        </span>
      }
      size="lg"
      footer={
        <div
          className="toolbar"
          style={{ gap: 8, justifyContent: "flex-end" }}
        >
          <button type="button" className="btn btn--sm" onClick={onClose}>
            cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={submit}
            disabled={!canSubmit || busy}
          >
            <KeyRound />
            {busy ? "saving…" : submitLabel}
          </button>
        </div>
      }
    >
      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        }}
      >
        <Field label="Name">
          <input
            value={draft.name}
            placeholder={defaultNameForHost(host)}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </Field>
        <Field label="Registry host">
          <input
            list="registry-suggestions-inline"
            value={draft.registry}
            onChange={(e) =>
              setDraft({ ...draft, registry: e.target.value })
            }
          />
          <datalist id="registry-suggestions-inline">
            {REGISTRY_SUGGESTIONS.map((h) => (
              <option key={h} value={h} />
            ))}
          </datalist>
        </Field>
        <Field label="Username">
          <input
            value={draft.username}
            placeholder={
              isDockerHub(host)
                ? "docker-hub-user"
                : host === "ghcr.io"
                  ? "github-username"
                  : "registry-username"
            }
            onChange={(e) =>
              setDraft({ ...draft, username: e.target.value })
            }
          />
        </Field>
        <Field
          label={
            host === "ghcr.io"
              ? editing
                ? "New GitHub PAT (leave blank to keep current)"
                : "GitHub PAT (read:packages)"
              : editing
                ? "New password (leave blank to keep current)"
                : "Password / Access token"
          }
        >
          <input
            type="password"
            value={draft.password ?? ""}
            placeholder={
              editing && existing?.password_last4
                ? `current ends in …${existing.password_last4}`
                : host === "ghcr.io"
                  ? "ghp_… or github_pat_…"
                  : "••••••••"
            }
            autoComplete="new-password"
            onChange={(e) =>
              setDraft({ ...draft, password: e.target.value })
            }
          />
        </Field>
        <Field label="Note (optional)" full>
          <input
            value={draft.note ?? ""}
            placeholder={`e.g. "${host} read-only token, rotated 2026-05"`}
            onChange={(e) =>
              setDraft({ ...draft, note: e.target.value || null })
            }
          />
        </Field>
        {editing && (
          <Field label="Status" full>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
              }}
            >
              <input
                type="checkbox"
                checked={draft.enabled ?? true}
                onChange={(e) =>
                  setDraft({ ...draft, enabled: e.target.checked })
                }
              />
              <span className="muted">
                Enabled — uncheck to temporarily stop using this
                credential without deleting it.
              </span>
            </label>
          </Field>
        )}
        {host === "ghcr.io" && (
          <div
            className="muted"
            style={{
              gridColumn: "1 / -1",
              fontSize: 11,
              padding: "8px 10px",
              border: "1px dashed var(--edge-strong)",
              borderRadius: 6,
            }}
          >
            <strong>GHCR tip:</strong> create the PAT at{" "}
            <a
              href="https://github.com/settings/tokens"
              target="_blank"
              rel="noreferrer"
            >
              github.com/settings/tokens
            </a>{" "}
            with only the <code>read:packages</code> scope.{" "}
            <code>repo</code> is <em>not</em> required for pulling
            private container images.
          </div>
        )}
        {error && (
          <div
            className="flash"
            style={{ gridColumn: "1 / -1", fontSize: 12 }}
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

interface FieldProps {
  label: React.ReactNode;
  full?: boolean;
  children: React.ReactNode;
}

function Field({ label, full, children }: FieldProps) {
  return (
    <label
      className="stack"
      style={{
        display: "grid",
        gap: 4,
        gridColumn: full ? "1 / -1" : undefined,
      }}
    >
      <span className="muted" style={{ fontSize: 12 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function defaultNameForHost(host: string): string {
  if (!host) return "";
  // Slug-friendly: drop port suffixes and any dots — operators tend
  // to type "ghcr-spiral-ai", "dockerhub-myorg", etc.
  const stripped = host.split(":")[0];
  if (isDockerHub(stripped)) return "dockerhub";
  if (stripped === "ghcr.io") return "ghcr";
  return stripped.replace(/\./g, "-");
}

/**
 * Seed the InlineRegistryEditor's form state. In "create" mode we
 * derive everything from the detected host; in "edit" mode we mirror
 * the existing row exactly so the operator can rotate the password
 * (or just toggle ``enabled``) without re-typing the rest.
 *
 * Note we intentionally never pull ``password`` out of the row — the
 * API never sends it (and ``password_last4`` is a fingerprint, not a
 * usable secret). Leaving ``draft.password`` empty is the signal the
 * controller's PUT handler interprets as "keep existing ciphertext".
 */
function buildInitialDraft(
  host: string,
  existing: ContainerRegistryAuthOut | undefined,
): ContainerRegistryAuthUpsertBody {
  if (existing) {
    return {
      name: existing.name,
      registry: existing.registry,
      username: existing.username,
      password: "",
      service_id: existing.service_id,
      pool_id: existing.pool_id,
      enabled: existing.enabled,
      note: existing.note,
    };
  }
  return {
    name: defaultNameForHost(host),
    registry: host,
    username: "",
    password: "",
    service_id: null,
    pool_id: null,
    enabled: true,
    note: null,
  };
}

/**
 * Listing helper used by the workload editor to render alternative
 * matches in a tooltip. Exported so future surfaces (e.g. the
 * read-only "live spec" card) can reuse the same lookup.
 */
export function matchedRegistryFor(
  image: string | null | undefined,
  rows: ContainerRegistryAuthOut[] | undefined,
): ContainerRegistryAuthOut | undefined {
  const host = parseImageHost(image);
  if (!host) return undefined;
  return (rows ?? []).find(
    (row) => row.scope === "global" && hostsMatch(row.registry, host),
  );
}
